import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Link } from "react-router-dom";
import { homeAction } from "@redux/modules";

// Yarışma
import { getMyCompetitions } from "@pages/private/Competition/competition.api";
import CompetitionBanner from "@pages/private/Competition/CompetitionBanner";

// Bootstrap
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

// React Icons
import { FaChevronLeft, FaChevronRight, FaTrophy } from 'react-icons/fa';

// import Swiper core and required modules
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

// Hooks
import useRoleAccess from '@hooks/useRoleAccess';

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Styles
import "./styles.scss";

/**
 * HomeAnnouncement Slider Bileşeni
 * 
 * Anasayfada duyuru slider'ını gösterir.
 * Rol bazlı filtreleme ile kullanıcının rolüne uygun duyuruları gösterir.
 * Kullanıcının katılabileceği süren bir yarışma varsa banner'ı ilk slide olur; banner görseli
 * olmayan yarışma slider'ın altında yazı (bağlantı) olarak gösterilir.
 *
 * @param {boolean} showAnnouncements - DUYURU bileşeni aktif mi
 * @param {boolean} showCompetitions - YARIŞMALAR bileşeni aktif ve /competitions erişilebilir mi
 */
const HomeAnnouncement = ({ showAnnouncements = true, showCompetitions = false }) => {
  const dispatch = useDispatch();
  const announcementList = useSelector((store) => store.home.announcementList);
  const loginSettings = useSelector((store) => store.auth.loginSettings);
  const [competitions, setCompetitions] = useState([]);
  
  // Rol erişim hook'u
  const { filterAnnouncementsByRole } = useRoleAccess();

  useEffect(() => {
    if (showAnnouncements) {
      dispatch(homeAction.getAnnouncementList("homepage"));
    }

    return () => {};
  }, [dispatch, showAnnouncements]);

  // Backend yalnızca kullanıcının hedef kitlesinde olduğu, şu an süren yarışmaları döner;
  // kazanma hakkını doldurduğu yarışmaya artık katılamayacağı için banner'ı gösterilmez.
  useEffect(() => {
    if (!showCompetitions) {
      setCompetitions([]);
      return undefined;
    }
    let active = true;
    getMyCompetitions()
      .then((res) => {
        if (!active) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setCompetitions(
          list.filter((item) => (item.winCount || 0) < (item.maxWinCount || 1))
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [showCompetitions]);

  /**
   * Rol bazlı filtrelenmiş duyuru listesi
   * Kullanıcının rolüne uygun duyuruları gösterir
   */
  const filteredAnnouncements = useMemo(() => {
    if (!showAnnouncements) return [];
    return filterAnnouncementsByRole(announcementList) || [];
  }, [announcementList, filterAnnouncementsByRole, showAnnouncements]);

  const bannerCompetitions = competitions.filter((item) => item.bannerImageUrl);
  const textCompetitions = competitions.filter((item) => !item.bannerImageUrl);
  const hasSlides =
    filteredAnnouncements.length > 0 || bannerCompetitions.length > 0;

  // Gösterilecek slide ya da yarışma yoksa bileşeni gösterme
  if (!hasSlides && textCompetitions.length === 0) {
    return null;
  }

  // Duyurular ve yarışmalar ayrı isteklerle geldiği için slide listesi sonradan değişebilir;
  // Swiper (loop) her değişimde yeniden kurulsun ve ilk slide yarışma olsun diye anahtar.
  const swiperKey = [
    ...bannerCompetitions.map((item) => `c${item.id}`),
    ...filteredAnnouncements.map((item, index) => `a${item.id || index}`),
  ].join("-");

  return (
    <section className="announcement-section pb-0">
      <Container>
        <Row>
          <Col>
            {hasSlides && (
            <div className="home-swiper-wrapper">
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }}
              loop={true}
              autoplay={{
                delay: 10000,
                disableOnInteraction: false,
              }}
              className="mySwiper home-swiper"
              key={swiperKey}
            >
              {bannerCompetitions.map((item) => (
                <SwiperSlide key={`competition-${item.id}`}>
                  <Link
                    to={`/competitions/${item.id}`}
                    className="home-competition-slide"
                    aria-label={item.name}
                  >
                    <CompetitionBanner competition={item} />
                  </Link>
                </SwiperSlide>
              ))}
              {filteredAnnouncements.map((item, index) => {
                const key = item.id || index;
                // "/" bağlantısı yönlendirme yapılmayacak duyuruları işaret eder
                const hasLink = item.url && item.url !== "/";
                // SwiperSlide, Swiper'ın doğrudan çocuğu olmalıdır. Fragment ile
                // sarmalandığında Swiper slide'ları güvenilir şekilde tanıyamayıp
                // bazı banner'ları göstermeyebiliyordu.
                return (
                  <SwiperSlide key={`announcement-${key}`}>
                    {item?.announcementContentList?.length > 0 &&
                      !hasLink && (
                        <Row className="justify-content-center">
                          <Col xs={12} sm={12}>
                            <img
                              src={item.announcementContentList[0]}
                              className="img-fluid w-100"
                              alt="announcement"
                            />
                          </Col>
                        </Row>
                      )}

                    {item?.announcementContentList?.length > 0 &&
                      hasLink && (
                        <Row className="justify-content-center">
                          <Col xs={12} sm={12} lg={12}>
                            <a
                              href={item.url}
                              target={item.newTab ? "_blank" : "_self"}
                              rel="noreferrer"
                            >
                              <img
                                src={item.announcementContentList[0]}
                                className="img-fluid w-100"
                                alt="announcement"
                              />
                            </a>
                          </Col>
                        </Row>
                      )}
                  </SwiperSlide>
                );
              })}
            </Swiper>
              <div className="swiper-button-next">
                <FaChevronRight style={{ color: `${loginSettings?.blue}` }} />
              </div>
              <div className="swiper-button-prev">
                <FaChevronLeft style={{ color: `${loginSettings?.blue}` }} />
              </div>
            </div>
            )}
            {textCompetitions.map((item) => (
              <CompetitionLink
                key={`competition-link-${item.id}`}
                competition={item}
                color={loginSettings?.blue}
              />
            ))}
          </Col>
        </Row>
      </Container>
    </section>
  );
};

/**
 * Banner görseli olmayan yarışma: slider'ın altında yazı; tıklanınca yarışma sayfasına gider.
 * Admin'in başlık / alt başlığı (başlık yoksa yarışma adı; alt başlık yoksa günlük hediye
 * sayısı).
 */
const CompetitionLink = ({ competition, color }) => (
  <Link
    to={`/competitions/${competition.id}`}
    className="home-competition-link"
    style={color ? { color } : undefined}
  >
    <FaTrophy className="home-competition-link__icon" aria-hidden="true" />
    <span>
      <strong className="home-competition-link__title">
        {competition.competitionTitle || competition.name}
      </strong>
      {" — "}
      {competition.competitionSubTitle ||
        (competition.dailyGiftCount
          ? `Her gün ${competition.dailyGiftCount} hediye!`
          : "Yarışma başladı!")}
    </span>
    <span className="home-competition-link__cta">Yarışmaya Katıl ›</span>
  </Link>
);

export default HomeAnnouncement;
